import api from './api';

// Use API gateway for barcode requests (routes to product-service via /api/barcode)

export interface BarcodeData {
  format: string;
  data: string;
  displayText: string;
  productName: string;
  productNameChinese: string;
  serialNumber?: string;
}

export interface BarcodeFormat {
  id: number;
  description: string;
  total_length: number;
  product_id_start: number;
  product_id_length: number;
  product_weight_start?: number;
  product_weight_length?: number;
  product_weight_decimal?: number;
  total_length_sub_unit?: number;
  product_id_start_sub_unit?: number;
  product_id_length_sub_unit?: number;
  variable_total_length?: string;
}

/**
 * GS1-128 Application Identifiers used in our barcodes:
 * - AI 01: GTIN (14 digits)
 * - AI 3202: Net weight in lbs with 2 decimal places (6 digits)
 * - AI 10: Batch/Date code (5 digits in our system: (100-YY) + DDD)
 * - AI 21: Serial number (6-digit HHMMSS print timestamp)
 */

/**
 * Compute the GS1-128 serial number based on print time.
 * Returns a 6-digit HHMMSS string (e.g., 14:35:07 → "143507").
 */
export const computeSerialNumber = (): string => {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  return `${hours}${minutes}${seconds}`;
};

// Re-export formatGS1Barcode from barcodeUtils for backwards compatibility
export { formatGS1Barcode } from '../utils/barcodeUtils';

const barcodeService = {
  /**
   * Generate barcode for a product
   * @param itemNumber Product SKU/item number
   * @param useSubUnit Whether to use sub-unit barcode
   * @param weight Optional weight for variable weight items
   * @param dateCode Optional date code from client (format: YYDDD, e.g., 74006 for 2026 day 6)
   * @param serialNumber Optional unique serial number (lot-box) for individual box identification
   */
  generateBarcode: async (
    itemNumber: string,
    useSubUnit: boolean = false,
    weight?: number,
    dateCode?: string,
    serialNumber?: string
  ): Promise<BarcodeData> => {
    const params = new URLSearchParams({
      useSubUnit: useSubUnit.toString()
    });

    if (weight !== undefined) {
      params.append('weight', weight.toString());
    }

    if (dateCode) {
      params.append('dateCode', dateCode);
    }

    if (serialNumber) {
      params.append('serialNumber', serialNumber);
    }

    // Call product service via API gateway
    const response = await api.get(`/barcode/product/${itemNumber}?${params.toString()}`);
    return response.data.data;
  },

  /**
   * Get all barcode formats
   */
  getBarcodeFormats: async (): Promise<BarcodeFormat[]> => {
    const response = await api.get('/barcode/formats');
    return response.data.data;
  },

  /**
   * Get specific barcode format by ID
   */
  getBarcodeFormatById: async (id: string): Promise<BarcodeFormat> => {
    const response = await api.get(`/barcode/formats/${id}`);
    return response.data.data;
  }
};

export default barcodeService;
